import {
  Component,
  computed,
  signal,
  input,
  model,
  output,
  viewChild,
  ElementRef,
  inject
} from '@angular/core';

import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { PlatformService } from '../../services/platform.service';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { ConfirmBodyComponent } from '../confirm/confirm-body.component';
import { ConfirmButtonCancelComponent } from '../confirm/confirm-button-cancel.component';
import { ConfirmTitleComponent } from '../confirm/confirm-title.component';
import { ConfirmButtonOkComponent } from '../confirm/confirm-button-ok.component';
import { ConfirmButtonsComponent } from '../confirm/confirm-buttons.component';
import { ConfirmComponent } from '../confirm/confirm.component';
import { ModalService } from '../modal/modal.service';

import type { ModalRef } from '../modal/modal-ref';
import type { ControlValueAccessor } from '@angular/forms';
import type {
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  WritableSignal
} from '@angular/core';

export interface SelectedFile {
  file: File;
  fileName: string;
}

export interface FilesTarget extends EventTarget {
  files?: FileList;
}
export interface EventTargetWithFiles extends Event {
  target: FilesTarget
}

export type FileUploadSize = 'small' | 'large';

/**
 * File upload component.
 *
 * Follows the core dual-support control shape — see
 * `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 *
 * - `value` is the single source of truth. It is what the template renders, what
 *   `writeValue()` writes, and what user interaction updates.
 * - Because it is a `model()` named `value`, this component already satisfies
 *   Angular Signal Forms' native custom-control contract.
 * - The `ControlValueAccessor` implementation is a thin adapter over that model,
 *   required only by `[(ngModel)]` / `[formControl]`.
 */
@Component({
  selector: 'td-wb-file-upload',
  templateUrl: 'file-upload.component.html',
  styleUrls: ['file-upload.component.scss'],
  imports: [
    IconComponent,
    ButtonComponent,
    IconButtonComponent,
    ConfirmComponent,
    ConfirmTitleComponent,
    ConfirmBodyComponent,
    ConfirmButtonsComponent,
    ConfirmButtonCancelComponent,
    ConfirmButtonOkComponent,
    TranslateDirective,
    TranslatePipe
  ],
  providers: [ValueAccessorProviderFactory(FileUploadComponent)],
  host: {
    '(drop)': 'onDrop($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '[class.td-wb-file-upload--small]': 'isSizeSmall()'
  }
})
export class FileUploadComponent implements ControlValueAccessor {
  private readonly modalService: ModalService = inject<ModalService>(ModalService);
  private readonly platformService: PlatformService = inject<PlatformService>(PlatformService);

  constructor() {
    // When on android/ios will prevent allowDragDrop=true directly on template
    this.native = this.platformService.isMobileOrTabletPortraitView();
  }

  readonly browseButton: Signal<ElementRef<HTMLButtonElement>> = viewChild<ElementRef<HTMLButtonElement>>('browseButton');
  readonly fileUpload: Signal<ElementRef<HTMLInputElement>> = viewChild<ElementRef<HTMLInputElement>>('fileUpload');
  readonly fileUploadCamera: Signal<ElementRef<HTMLInputElement>> = viewChild<ElementRef<HTMLInputElement>>('fileUploadCamera');

  /**
   * Selected files. Single source of truth, and the Signal Forms native
   * custom-control binding.
   */
  readonly value: ModelSignal<Array<SelectedFile>> = model<Array<SelectedFile>>([]);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  /**
   * Defines the types of files the server accepts.
   * @default image/*,application/pdf
   * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept
   */
  accept: InputSignal<string> = input<string>('image/*,application/pdf');

  /**
   * Defines if we allow Drag and Drop into the component
   * @default true
   */
  allowsDragDrop: InputSignal<boolean> = input<boolean>(true);

  /**
   * Sets component to accept multiple files
   * Please note that this does NOT represent the `multiple` attribute of the `input` element,
   * so the user has to select a single file at a time, but the component can hold multiple files.
   * @default false
   */
  multiple: InputSignal<boolean> = input<boolean>(false);

  /**
   * Disables the component
   * @default false
   */
  readonly disabledInput: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  /** Disabled state pushed imperatively by a form owner through `setDisabledState()`. */
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);

  /**
   * Effective disabled state. Signal Forms writes both the `disabled` input and
   * `setDisabledState()` — reconcile, never pick one.
   */
  readonly disabled: Signal<boolean> = computed(
    (): boolean => this.disabledInput() || this.disabledByOwner()
  );

  /**
   * Defines the name of the input element
   * @default 'file-upload'
   */
  name: InputSignal<string> = input<string>('file-upload');

  /**
   * Defines a message to be displayed informing user of upload capabilities
   * @example [message]="'tdWb.RESP.institutions.uploadFile'|translate"
   */
  message: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Sets the size of the file upload component.
   * Options: 'small', 'large'.
   * @default 'large'
   */
  size: InputSignal<FileUploadSize> = input<FileUploadSize>('large');

  readonly onUploadCancel: OutputEmitterRef<void> = output<void>();
  readonly onBrowseFiles: OutputEmitterRef<void> = output<void>();

  // Internal variables
  isDragging: WritableSignal<boolean> = signal(false);
  isDraggingFirst: WritableSignal<boolean> = signal(false);
  openedModal: ModalRef<unknown>;
  actionMenuOpen: boolean = false;
  fileToDelete: string;
  readonly native: boolean;

  isSizeSmall(): boolean {
    return this.size() === 'small';
  }

  onBrowseFileButtonClick(): void {
    this.fileUpload().nativeElement.click();
    this.onBrowseFiles.emit();
  }

  actionMenuToggle(value: boolean): void {
    this.actionMenuOpen = value;
  }

  /* Handler for the user file selection */
  // Observation: Even though we are allowing multiple, the user can only select one file at a time
  onFileSelected(event: EventTargetWithFiles): void {
    if (event.target.files !== undefined && !this.disabled()) {
      const file: File = event.target.files[0];
      const current = this.value() ?? [];

      if (current.length === 0 || !this.multiple()) {
        this.value.set([{ file, fileName: file.name }]);
      } else {
        let name = file.name;

        // For multiple files scenario on iOS devices and taking pictures
        //   iOS always sends us the name as "image.jpg",
        // So lets rename it to a large random number like android does
        // Drawback: as we cant differentiate camera from selection,
        //   there is a risk that users could pick a file named image.jpg and see the name replaced
        if (this.native && this.platformService.isIOS() && this.multiple() && current.length > 0 && file.name === 'image.jpg') {
          name = `${Math.floor(Math.random() * 1000000000000)}.jpg`;
        }
        this.value.set([...current, { file, fileName: name }]);
      }
      this.notifyUserInteraction();
    }
    this.fileUpload().nativeElement.value = ''; // clears out the hidden input
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  /** Owner -> control. Must never emit onChangeCallback. */
  writeValue(value: Array<SelectedFile> | null | undefined): void {
    this.value.set(value ?? []);
  }

  /** Control -> owner. */
  registerOnChange(fn: (value: Array<SelectedFile>) => void): void {
    this.onChangeCallback = fn;
  }

  /** Control -> owner. */
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  /** Owner -> control. */
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  private onChangeCallback: (value: Array<SelectedFile>) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  private notifyUserInteraction(): void {
    this.onChangeCallback(this.value());
    this.onTouchedCallback();
    this.touch.emit();
  }

  protected remove(template: TemplateRef<unknown>, file: SelectedFile, event: MouseEvent|KeyboardEvent): void {
    event.preventDefault();
    this.fileToDelete = file.fileName;
    this.openedModal = this.modalService.open(template, {
      size: 'extra-small'
    });

    this.openedModal.afterClose().subscribe((result: unknown): void => {
      if (result === 'OK') {
        this.value.set((this.value() ?? []).filter((f: SelectedFile): boolean => f !== file));
        this.notifyUserInteraction();
      }
      this.browseButton()?.nativeElement?.focus();
    });
    this.onUploadCancel.emit();
  }

  protected onConfirmationClose(): void {
    this.openedModal.close();
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowsDragDrop() || this.disabled()) {
      return;
    }
    this.isDragging.set(false);
    this.isDraggingFirst.set(false);
    const files = event.dataTransfer.files;
    this.onFileSelected({ target: { files } } as EventTargetWithFiles);
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowsDragDrop() || this.disabled()) {
      return;
    }
    if (this.isTargetArea(event.target)) {
      this.isDragging.set(true);
      this.isDraggingFirst.set((this.value() ?? []).length === 0);
    }
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowsDragDrop() || this.disabled() || !this.isTargetArea(event.target)) {
      return;
    } else {
      this.isDragging.set(false);
      this.isDraggingFirst.set(false);
    }
  }

  private isTargetArea(target: EventTarget): boolean {
    return (target as Element).id === 'td-wb-file-upload-area'
  }
}
